import { MatterDto, MatterForm, PersonalRateDraft, PricelistTabDraft } from 'types/matters';
import { MatterPricelist, PricingKind } from 'types/pricelists';
import { inForceListOn } from '../pricelists/modal/matterPricelistEditorUtils';

// A follower's exceptions stay flagged, so the copy takes them over with the link.
const toDrafts = (list: MatterPricelist): PersonalRateDraft[] =>
    list.personalRates.map((row) => ({
        employee: row.employee,
        jobRole: row.jobRole,
        hourlyPrice: row.hourlyPrice,
        ...(row.isException ? { isException: true } : {}),
    }));

/**
 * What a copied case takes over from the source, as the tabs the form stages: the list in force on the new
 * opening date becomes the base tab, every later list a dated tab, and lists that ended before that date
 * are not copied. No tab carries a source list's id: the create writes them all as new lists.
 */
export const seedCopiedPricing = (lists: MatterPricelist[], openedDate: string): PricelistTabDraft[] => {
    const inForce = inForceListOn(lists, openedDate);
    return [
        {
            key: 'base',
            id: null,
            isBase: true,
            validFrom: null,
            name: null,
            rates: inForce?.rates ?? [],
            personalRates: inForce ? toDrafts(inForce) : [],
        },
        ...lists
            .filter((list) => list.validFrom !== null && list.validFrom > openedDate)
            .map((list) => ({
                key: `copied-${list.id}`,
                pricingKind: list.pricingKind,
                id: null,
                isBase: false,
                validFrom: list.validFrom,
                name: list.name,
                rates: list.rates,
                personalRates: toDrafts(list),
            })),
    ];
};

export interface FittedPricing {
    pricelists: PricelistTabDraft[];
    droppedPeopleNames: string[];
}

/**
 * At submit of a case started from another, every personal row of a person no longer active is dropped from every tab; the
 * names come back so the form can say who it left out. Anyone else stays, on the case team or not. The lists
 * themselves are never dropped: a tab the opening date has caught up with is refused instead.
 */
export function dropInactivePersonalRates(
    pricelists: PricelistTabDraft[],
    inactiveEmployeeIds: Set<number>
): FittedPricing {
    const droppedPeople = new Map<number, string>();
    const keepActive = (rows: PersonalRateDraft[]): PersonalRateDraft[] =>
        rows.filter((row) => {
            if (!inactiveEmployeeIds.has(row.employee.id)) {
                return true;
            }
            droppedPeople.set(row.employee.id, row.employee.name);
            return false;
        });
    return {
        pricelists: pricelists.map((tab) => ({ ...tab, personalRates: keepActive(tab.personalRates) })),
        droppedPeopleNames: [...droppedPeople.values()],
    };
}

/** Everyone the source's lists price, once each: the people whose active state the copy has to read. */
export function pricedPeopleOf(lists: MatterPricelist[]): number[] {
    return [...new Set(lists.flatMap((list) => list.personalRates.map((row) => row.employee.id)))];
}

/**
 * A case started from another keeps its kind and its link: a follower's copy follows the same list, and the copy
 * of a case with its own prices stages them as tabs, its person rows and dated lists included.
 */
export function copiedPricingLink(
    source: Pick<MatterDto, 'pricingKind' | 'followsPricelist'>
): Pick<MatterForm, 'pricingKind' | 'followsPricelist'> {
    return {
        pricingKind: source.pricingKind ?? PricingKind.ROLE,
        followsPricelist: source.followsPricelist ?? null,
    };
}
